import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  EmojiEvents as TrophyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AREA_COLORS = {
  matematicas: '#FF6B6B',
  'lectura-critica': '#4ECDC4',
  sociales: '#FFE66D',
  ciencias: '#95E1D3',
  ingles: '#C7CEEA',
  completo: '#845EC2',
};

const AREA_NAMES = {
  matematicas: 'Matemáticas',
  'lectura-critica': 'Lectura Crítica',
  sociales: 'Sociales y Ciudadanas',
  ciencias: 'Ciencias Naturales',
  ingles: 'Inglés',
  completo: 'Simulacro Completo',
};

const HistorialPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsByArea, setStatsByArea] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [filterArea, setFilterArea] = useState('todas');

  useEffect(() => {
    fetchResults();
    fetchStatsByArea();
  }, [filterArea]);

  const fetchResults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/results/my-results?area=${filterArea}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(response.data.results);
      setStats(response.data.stats);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setLoading(false);
    }
  };

  const fetchStatsByArea = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/results/stats/by-area`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatsByArea(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreEmoji = (score) => {
    if (score >= 90) return '🏆';
    if (score >= 80) return '🌟';
    if (score >= 70) return '👍';
    if (score >= 60) return '👌';
    return '📚';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando historial...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          📊 Mi Historial de Resultados
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Revisa tu progreso y rendimiento en los simulacros
        </Typography>
      </Box>

      {/* Estadísticas Generales */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#845EC2', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrophyIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Exámenes</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {stats.total_exams}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#4ECDC4', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Promedio
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {stats.average_score}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#FFE66D' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Mejor Puntaje
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {stats.best_score}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#95E1D3' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ScheduleIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Tiempo Total</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {formatTime(stats.total_time_spent)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs para filtrar por área */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => {
            setSelectedTab(newValue);
            const areas = ['todas', 'completo', 'matematicas', 'lectura-critica', 'sociales', 'ciencias', 'ingles'];
            setFilterArea(areas[newValue]);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Todas" />
          <Tab label="Simulacro Completo" />
          <Tab label="Matemáticas" />
          <Tab label="Lectura Crítica" />
          <Tab label="Sociales" />
          <Tab label="Ciencias" />
          <Tab label="Inglés" />
        </Tabs>
      </Paper>

      {/* Estadísticas por Área */}
      {Object.keys(statsByArea).length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            📈 Estadísticas por Área
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(statsByArea).map(([area, areaStats]) => (
              <Grid item xs={12} sm={6} md={4} key={area}>
                <Card
                  sx={{
                    borderLeft: `4px solid ${AREA_COLORS[area]}`,
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {AREA_NAMES[area]}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Exámenes:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {areaStats.total_exams}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Promedio:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {areaStats.average_score}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Mejor:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {areaStats.best_score}%
                      </Typography>
                    </Box>
                    {areaStats.improvement !== 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        {areaStats.improvement > 0 ? (
                          <>
                            <TrendingUpIcon color="success" fontSize="small" />
                            <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                              +{areaStats.improvement}% de mejora
                            </Typography>
                          </>
                        ) : (
                          <>
                            <TrendingDownIcon color="error" fontSize="small" />
                            <Typography variant="body2" color="error.main" sx={{ ml: 0.5 }}>
                              {areaStats.improvement}%
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Tabla de Resultados */}
      {results.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Área</TableCell>
                <TableCell align="center">Puntaje</TableCell>
                <TableCell align="center">Correctas</TableCell>
                <TableCell align="center">Incorrectas</TableCell>
                <TableCell align="center">Tiempo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((result) => (
                <TableRow
                  key={result.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/historial/${result.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(result.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={AREA_NAMES[result.area]}
                      size="small"
                      sx={{
                        bgcolor: AREA_COLORS[result.area],
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mr: 1 }}>
                        {result.score}%
                      </Typography>
                      <Typography variant="h6">{getScoreEmoji(result.score)}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={result.score}
                      color={getScoreColor(result.score)}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<CheckIcon />}
                      label={result.correct_answers}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<CancelIcon />}
                      label={result.incorrect_answers}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {formatTime(result.time_spent)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          No tienes resultados guardados todavía. ¡Realiza tu primer simulacro!
        </Alert>
      )}
    </Container>
  );
};

export default HistorialPage;
